import { useState } from "react";
import { Link } from "react-router-dom";
import { Send, CheckCircle2, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import { REVIEW_TIERS } from "@/lib/reviewPricing";

/**
 * EnterprisePage: "Enterprise: talk to us" (concierge mode, 2026-10-09).
 *
 * Hero with an honest "what we offer today" card → contact form → dark
 * final CTA.
 *
 * The previous version claimed a security certification, SSO, 7-year
 * retention, a quarterly plan price, customer logos and three case studies
 * with stats.
 * None of it existed, so it was deleted. Do not add certifications, logos,
 * customers or stats here until they are real and documented.
 *
 * Backend contract (`POST /api/enterprise/inquiry`) is preserved: every
 * formData key still ships. `service_type` is not a field of the backend
 * `EnterpriseInquiry` model (it is dropped server-side), so it is no longer
 * shown as a choice.
 */
export default function EnterprisePage() {
  const [loading, setLoading]     = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData]   = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    company_size: "201-1000",
    industry: "",
    needs: "",
    budget_range: "",
    service_type: "ai_product_review",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.company_name || !formData.contact_name || !formData.email || !formData.needs) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enterprise/inquiry`, formData);
      setSubmitted(true);
      toast.success("Inquiry received. We'll reply by email.");
    } catch {
      toast.error("Couldn't submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const set = (key, value) => setFormData((p) => ({ ...p, [key]: value }));

  const scrollToContact = () => {
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-canvas text-ink font-body">
      <MarketingNav />

      <Hero onContact={scrollToContact} />
      <ContactForm
        formData={formData}
        set={set}
        loading={loading}
        submitted={submitted}
        handleSubmit={handleSubmit}
      />
      <FinalCTA onContact={scrollToContact} />

      <MarketingFooter />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hero: eyebrow + headline + what we offer today
// ---------------------------------------------------------------------------

const OFFER_TODAY = [
  "Async reviews of AI-built code and product or strategy work",
  "A named reviewer on every review; today that is the founder, Ankit Kumar",
  "List prices per review, the same for teams",
  "We sign your NDA on request",
  "Verdicts private by default",
];

function Hero({ onContact }) {
  return (
    <section className="relative overflow-hidden">
      <div aria-hidden="true"
           className="absolute -top-32 -right-24 w-[640px] h-[640px] rounded-full bg-brand-green/10 blur-3xl pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-16 sm:pb-24">
        <div className="grid lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-16 items-start">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-green">
              The human review layer for AI-built work
            </p>
            <h1 className="font-display text-5xl sm:text-6xl text-ink leading-[1.05] mt-5">
              Reviewing AI work as a team?{" "}
              <em className="not-italic">
                <span className="italic text-brand-green">Talk to us.</span>
              </em>
            </h1>
            <p className="mt-6 text-lg text-slateline-600 leading-relaxed max-w-xl">
              Xpert is early and founder-run. If your team ships AI-built code or
              product and strategy work and wants a named human to review it, tell
              us what you need. We'll say plainly whether we can help today.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={onContact}
                      className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-brand-green hover:bg-brand-greenHover text-white font-semibold text-sm shadow-sm transition-colors">
                Talk to us <ArrowRight className="w-4 h-4" />
              </button>
              <Link to="/get-verified"
                    className="inline-flex items-center gap-2 h-12 px-6 rounded-md border border-slateline-300 hover:border-ink text-ink font-medium text-sm transition-colors">
                Or submit one review now
              </Link>
            </div>
          </div>

          <aside className="bg-white border border-slateline-200 rounded-xl p-7 shadow-sm">
            <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slateline-500 mb-4">
              What we offer today
            </div>
            <ul className="space-y-3">
              {OFFER_TODAY.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-ink leading-snug">
                  <Check className="w-4 h-4 text-brand-green flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-sm text-slateline-600">
              {REVIEW_TIERS.map((t) => `$${t.price} for ${t.label}`).join(" · ")}
            </p>
            <div className="mt-5 pt-4 border-t border-slateline-100 text-[13px] text-slateline-600 leading-relaxed">
              Our <Link to="/legal/dpa" className="text-brand-green hover:underline">DPA</Link> and{" "}
              <Link to="/legal/subprocessors" className="text-brand-green hover:underline">sub-processor list</Link>{" "}
              are public. We don't offer SSO, security certifications or volume
              contracts today; if you need them, say so in the form.
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Contact form — preserves backend contract
// ---------------------------------------------------------------------------

function ContactForm({ formData, set, loading, submitted, handleSubmit }) {
  return (
    <section id="contact" className="bg-white border-t border-slateline-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <SectionHead
          eyebrow="Talk to us"
          title="Tell us about your AI workflow."
          subtitle="A short form. We reply by email."
        />

        <div className="mt-10 mx-auto max-w-3xl">
          {submitted ? (
            <div className="text-center bg-white border border-slateline-200 rounded-xl p-12 shadow-sm">
              <CheckCircle2 className="w-14 h-14 text-brand-green mx-auto mb-5" />
              <h3 className="font-display text-3xl text-ink">Thanks. We've got it.</h3>
              <p className="text-slateline-600 mt-3 max-w-md mx-auto">
                We'll reply by email to understand what you need and whether we can help today.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}
                  className="bg-white border border-slateline-200 rounded-xl p-7 sm:p-12 shadow-sm space-y-5">
              <FormRow>
                <Field label="Company name *">
                  <Input value={formData.company_name}
                         onChange={(v) => set("company_name", v)}
                         placeholder="e.g. Acme Corp" />
                </Field>
                <Field label="Your name *">
                  <Input value={formData.contact_name}
                         onChange={(v) => set("contact_name", v)}
                         placeholder="First and last" />
                </Field>
              </FormRow>

              <FormRow>
                <Field label="Work email *">
                  <Input type="email" value={formData.email}
                         onChange={(v) => set("email", v)}
                         placeholder="you@company.com" />
                </Field>
                <Field label="Company size">
                  <Select value={formData.company_size}
                          onChange={(v) => set("company_size", v)}
                          options={[
                            ["1-10", "1–10"], ["11-50", "11–50"],
                            ["51-200", "51–200"], ["201-1000", "201–1,000"],
                            ["1001-5000", "1,001–5,000"], ["5000+", "5,000+"],
                          ]} />
                </Field>
              </FormRow>

              <Field label="Describe what you're trying to solve *">
                <textarea
                  value={formData.needs}
                  onChange={(e) => set("needs", e.target.value)}
                  rows={5}
                  placeholder="A paragraph is enough. What kind of AI work, how often, what's blocking it today."
                  className={textareaCls}
                />
                <p className="text-[11px] text-slateline-500 mt-1.5">
                  We won't share this.
                </p>
              </Field>

              <div className="flex items-center justify-between gap-4 pt-2 flex-wrap">
                <span className="text-xs text-slateline-500 inline-flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-brand-green" />
                  No sales sequence. No demo gate.
                </span>
                <button
                  type="submit" disabled={loading}
                  className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md
                             bg-brand-green hover:bg-brand-greenHover disabled:opacity-60
                             text-white font-semibold text-sm tracking-wide shadow-sm transition-colors"
                >
                  {loading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Final dark CTA
// ---------------------------------------------------------------------------

function FinalCTA({ onContact }) {
  return (
    <section className="relative bg-ink text-white overflow-hidden">
      <div aria-hidden="true"
           className="absolute -bottom-32 -left-24 w-[640px] h-[640px] rounded-full bg-brand-green/25 blur-3xl pointer-events-none" />
      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-green">
          The human review layer for AI-built work
        </p>
        <h2 className="font-display text-4xl sm:text-5xl text-white leading-tight mt-3">
          A named human<br />
          <em className="not-italic">
            <span className="italic text-brand-green">on your team's AI work.</span>
          </em>
        </h2>
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <button onClick={onContact}
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-brand-green hover:bg-brand-greenHover text-white font-semibold text-sm shadow-sm transition-colors">
            Talk to us <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

function SectionHead({ eyebrow, title, subtitle }) {
  return (
    <div className="max-w-3xl">
      <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-green">{eyebrow}</p>
      <h2 className="font-display text-4xl sm:text-[44px] text-ink leading-[1.1] mt-4">{title}</h2>
      {subtitle && (
        <p className="text-lg text-slateline-600 mt-4 leading-relaxed">{subtitle}</p>
      )}
    </div>
  );
}

function FormRow({ children }) {
  return <div className="grid sm:grid-cols-2 gap-4">{children}</div>;
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slateline-700">{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

const inputCls = "w-full bg-white border border-slateline-200 rounded-md px-3 h-11 " +
  "text-ink placeholder-slateline-400 outline-none " +
  "focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 transition-colors";
const textareaCls = inputCls.replace("h-11", "min-h-[110px] py-3");

function Input({ value, onChange, placeholder, type = "text" }) {
  return (
    <input
      type={type} value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={inputCls}
    />
  );
}

function Select({ value, onChange, options }) {
  return (
    <select value={value}
            onChange={(e) => onChange(e.target.value)}
            className={`${inputCls} pr-8`}>
      {options.map(([val, label]) => (
        <option key={val} value={val}>{label}</option>
      ))}
    </select>
  );
}
